<script lang="ts" setup>
  import { onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import type { TimeTrackingJsonldDefaultRead } from '@/api'
  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'

  import type { MenuItem } from 'primevue/menuitem'
  import TrackingAccordionTitle from '@/components/timetracking/TrackingAccordionTitle.vue'
  import { useGeneratedTimeTrackingStampStore, useGeneratedTimeTrackingStore } from '@/stores'
  import { useTitrop } from '@/composables/useTitrop.ts'

  const titrop = useTitrop()
  const { t } = useI18n()
  const timeTrackingStore = useGeneratedTimeTrackingStore()

  const timeTrackings = ref<TimeTrackingJsonldDefaultRead[] | undefined>()

  const loading = ref(true)

  async function refresh() {
    loading.value = true
    timeTrackings.value = await timeTrackingStore
      .findAll(
        {
          query: {
            employee: titrop.value.user.currentIri
          }
        },
        { forceRefresh: true }
      )
      .then((d) => d.value)

    //this is just for preloading - makes the ui way faster
    const relatedStamps = timeTrackings.value.map((track) => track.lastStamp).filter((a) => a !== undefined && a !== null) as string[] | undefined
    if (relatedStamps?.length)
      await useGeneratedTimeTrackingStampStore()
        .fetchAllByIri(relatedStamps)
        .then((d) => d.value)

    loading.value = false
  }

  onMounted(async () => {
    await refresh()
  })

  const contextMenuRef = ref()
  const contextMenuItems: MenuItem[] = [
    {
      label: t('refresh'),
      command: async () => {
        await refresh()
      }
    }
  ]
</script>

<template>
  <WidgetSkeleton v-if="loading" variant="tracking" />
  <div
    v-else
    style="height: inherit"
    @contextmenu.prevent="contextMenuRef.show($event)">
    <ul class="space-y-2">
      <li
        v-for="(ttrack, idx) in timeTrackings"
        :key="ttrack['@id']"
        class="border rounded-lg hover:bg-gray-50 cursor-pointer px-0.5">
        <RouterLink
          :to="{ name: 'time-tracking', query: { t: ttrack.id } }"
          class="text-current">
          <TrackingAccordionTitle
            :index="idx"
            :track="ttrack"
            reduced />
        </RouterLink>
      </li>
    </ul>
    <ContextMenu
      ref="contextMenuRef"
      :model="contextMenuItems" />
  </div>
</template>

<style scoped></style>

<i18n>
{
  "de": { "refresh": "Aktualisieren" },
  "en": { "refresh": "Refresh" }
}
</i18n>
